import React, { useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useHistory, useParams } from 'react-router-dom';
import Breadcrumbs from '~/components/Breadcrumbs/Breadcrumbs';
import CobuSelect from '~/components/CobuSelect/CobuSelect';
import ImageDropzone from '~/components/ImageDropzone/ImageDropzone';
import Spinner from '~/components/Spinner/Spinner';
import { SelectType } from '~/enums/SelectType';
import { SelectedOptionType } from '~/types/form';
import { GlobalEventTemplateDTO } from '~/api/globalEventTemplates';
import {
  useCreateGlobalEventTemplate,
  useGlobalEventTemplates,
  useUpdateGlobalEventTemplate
} from '~/hooks/globalEventTemplates/useGlobalEventTemplates';

import styles from './styles.module.css';

const recommendedMonthOptions = [
  { label: 'January', value: 'January' },
  { label: 'February', value: 'February' },
  { label: 'March', value: 'March' },
  { label: 'April', value: 'April' },
  { label: 'May', value: 'May' },
  { label: 'June', value: 'June' },
  { label: 'July', value: 'July' },
  { label: 'August', value: 'August' },
  { label: 'September', value: 'September' },
  { label: 'October', value: 'October' },
  { label: 'November', value: 'November' },
  { label: 'December', value: 'December' }
];

type FormFields = {
  title: string;
  description: string;
  whatToBring?: string;
  howToUrl?: string;
  recommendedMonth?: string;
};

const LIST_PATH = '/building/global-event-templates';

const GlobalEventTemplateForm = () => {
  const { templateId } = useParams<{ templateId?: string }>();
  const history = useHistory();

  const templatesQuery = useGlobalEventTemplates();
  const createMutation = useCreateGlobalEventTemplate();
  const updateMutation = useUpdateGlobalEventTemplate();

  const template = useMemo(
    () =>
      templateId
        ? templatesQuery.data?.find((t) => t.uuid === templateId)
        : undefined,
    [templateId, templatesQuery.data]
  );

  const [image, setImage] = useState<File | string>('');
  const [selectedMonth, setSelectedMonth] = useState<string | undefined>();

  const { register, handleSubmit, errors, control, reset } =
    useForm<FormFields>();

  useEffect(() => {
    if (template) {
      reset({
        title: template.title,
        description: template.description,
        whatToBring: template.whatToBring || '',
        howToUrl: template.howToUrl || '',
        recommendedMonth: template.recommendedMonth || ''
      });
      setSelectedMonth(template.recommendedMonth);
    }
  }, [template, reset]);

  const submitHandler = (values: FormFields) => {
    const payload: GlobalEventTemplateDTO = {
      title: values.title,
      description: values.description,
      whatToBring: values.whatToBring || '',
      howToUrl: values.howToUrl || '',
      recommendedMonth: selectedMonth || ''
    };

    if (image) {
      payload.image = image;
    }

    if (templateId) {
      updateMutation.mutate(
        { templateId, templateData: payload },
        { onSuccess: () => history.push(LIST_PATH) }
      );
    } else {
      createMutation.mutate(payload, {
        onSuccess: () => history.push(LIST_PATH)
      });
    }
  };

  const handleDrop = (acceptedFiles: File[]) => {
    setImage(acceptedFiles[0]);
  };

  const isSubmitting = createMutation.isLoading || updateMutation.isLoading;
  const isLoadingTemplate =
    !!templateId && templatesQuery.isLoading && !template;

  if (isLoadingTemplate) {
    return (
      <div className='content-container'>
        <Spinner />
      </div>
    );
  }

  return (
    <div className='content-container'>
      {isSubmitting && <Spinner float />}
      <Breadcrumbs />
      <form onSubmit={handleSubmit(submitHandler)}>
        <div className={styles.header}>
          <h1>Global Event Template</h1>
          <div className={styles.formButtons}>
            <button
              className='btn-standard btn-primary margin-left-16 weight-500'
              type='submit'
              disabled={isSubmitting}
            >
              {templateId ? 'Save' : 'Create'}
            </button>
            <button
              className='btn-standard btn-seconday weight-500'
              type='button'
              onClick={() => history.push(LIST_PATH)}
            >
              Cancel
            </button>
          </div>
        </div>

        <label className='margin-top-16' htmlFor='title'>
          Gathering name
        </label>
        <input
          type='text'
          id='title'
          name='title'
          placeholder='E.g. Wine night in the Lobby'
          ref={register({ required: 'This field is required' })}
          className={`${errors.title ? 'border-red' : ''}`}
          defaultValue={template?.title}
        />

        <div className={styles.row}>
          <div className={styles.input}>
            <label className='margin-top-16' htmlFor='description'>
              Gathering description
            </label>
            <textarea
              id='description'
              name='description'
              cols={30}
              rows={3}
              ref={register({ required: 'This field is required' })}
              className={`${errors.description ? 'border-red' : ''}`}
              placeholder='E.g. Details about the gathering'
              defaultValue={template?.description}
            />
          </div>
          <div className={styles.input}>
            <label className='margin-top-16' htmlFor='whatToBring'>
              What to bring
            </label>
            <textarea
              id='whatToBring'
              name='whatToBring'
              cols={30}
              rows={3}
              ref={register}
              placeholder='E.g. Also, bring joy and good will.'
              defaultValue={template?.whatToBring}
            />
          </div>
        </div>

        <div className={styles.row}>
          <div className={styles.input}>
            <label className='margin-top-16' htmlFor='howToUrl'>
              How to link
            </label>
            <input
              type='text'
              id='howToUrl'
              name='howToUrl'
              ref={register}
              defaultValue={template?.howToUrl}
            />
          </div>
          <div className={styles.input}>
            <label
              className='margin-top-16 margin-bottom-8'
              htmlFor='recommendedMonth'
            >
              Recommended month
            </label>
            <Controller
              name='recommendedMonth'
              control={control}
              render={({ ref, onChange, ...props }) => (
                <CobuSelect
                  options={recommendedMonthOptions}
                  {...props}
                  onChange={(selectedOption: SelectedOptionType | null) => {
                    onChange(selectedOption?.value);
                    setSelectedMonth(selectedOption?.value);
                  }}
                  selectType={SelectType.Form}
                  defaultValue={
                    selectedMonth
                      ? { value: selectedMonth, label: selectedMonth }
                      : null
                  }
                />
              )}
            />
          </div>
        </div>

        <div>
          <label className='margin-top-16' htmlFor='image'>
            Add a photo
          </label>
          <div className={`${styles.imagePicker} margin-top-8`}>
            <ImageDropzone
              image={image || template?.image}
              handleDrop={handleDrop}
            />
          </div>
        </div>
      </form>
    </div>
  );
};

export default GlobalEventTemplateForm;
